import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import SEOHelmet from '../components/SEOHelmet';

const PrivacyPolicy: React.FC = () => {
  usePageTitle("Privacy Policy");

  return (
    <>
      <SEOHelmet
        title="Privacy Policy"
        description="Privacy Policy for CEC Track - Learn how we collect, use, and protect your personal information on our continuing education credits management platform."
        canonical="https://cectrack.com/privacy-policy"
        noindex={false}
      />
      <div className="container mt-4 mb-5">
      <h1 className="mb-4">Privacy Policy</h1>
      <p className="text-muted"><strong>Last Updated:</strong> November 5, 2025</p>

      <div className="mt-4">
        <h2 className="h4 mt-4">1. Introduction</h2>
        <p>
          CEC Track, Inc. ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains 
          how we collect, use, disclose, and safeguard your information when you use our CEC Track service ("Service").
        </p>
        <p>
          Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of 
          information in accordance with this policy.
        </p>

        <h2 className="h4 mt-4">2. Information We Collect</h2>
        
        <h3 className="h5 mt-3">2.1 Information You Provide</h3>
        <p>We collect information that you voluntarily provide when using our Service:</p>
        <ul>
          <li><strong>Account Information:</strong> Name, email address, phone number, profession, and company</li>
          <li><strong>Professional Information:</strong> License numbers, states of licensure, issue and expiration dates</li>
          <li><strong>Education Records:</strong> Class names, dates, credit hours, providers, course descriptions, and completion status</li>
          <li><strong>Renewal Cycles:</strong> Cycle names, start/end dates, state requirements, and credit tallies</li>
          <li><strong>Certificates:</strong> Uploaded PDF and image files of completion certificates</li>
          <li><strong>Notes and Comments:</strong> Any additional information you enter about classes or cycles</li>
        </ul>

        <h3 className="h5 mt-3">2.2 Automatically Collected Information</h3>
        <p>When you access the Service, we automatically collect certain information:</p>
        <ul>
          <li><strong>Log Data:</strong> IP address, browser type, operating system, pages visited, time spent, and referring URLs</li>
          <li><strong>Device Information:</strong> Device type, unique device identifiers, and mobile network information</li>
          <li><strong>Usage Data:</strong> Features used, actions taken, and interaction patterns within the Service</li>
          <li><strong>Cookies:</strong> Authentication tokens and session management data</li>
        </ul>

        <h3 className="h5 mt-3">2.3 Information from Third Parties</h3>
        <p>
          We do not currently collect information from third parties, but may do so in the future for authentication 
          services or integration with professional licensing boards (with your explicit consent).
        </p>

        <h2 className="h4 mt-4">3. How We Use Your Information</h2>
        <p>We use the collected information for the following purposes:</p>
        <ul>
          <li><strong>Service Provision:</strong> To provide, maintain, and improve the CEC tracking functionality</li>
          <li><strong>Account Management:</strong> To create and manage your user account</li>
          <li><strong>Communication:</strong> To send service-related notifications, updates, and support responses</li>
          <li><strong>Compliance Tracking:</strong> To calculate credit totals, track requirements, and alert you to deadlines</li>
          <li><strong>Storage and Organization:</strong> To store your certificates and education records securely</li>
          <li><strong>Analytics:</strong> To analyze usage patterns and improve Service features</li>
          <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
          <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
        </ul>

        <h2 className="h4 mt-4">4. Data Storage and Security</h2>
        
        <h3 className="h5 mt-3">4.1 Data Storage</h3>
        <p>
          Your data is stored using industry-standard cloud infrastructure services:
        </p>
        <ul>
          <li><strong>Database:</strong> MongoDB Atlas with encryption at rest</li>
          <li><strong>File Storage:</strong> Amazon S3 or equivalent cloud storage for certificate files</li>
          <li><strong>Application Hosting:</strong> Secure hosting platforms with SSL/TLS encryption</li>
        </ul>

        <h3 className="h5 mt-3">4.2 Security Measures</h3>
        <p>
          We implement reasonable security measures to protect your information:
        </p>
        <ul>
          <li>Password hashing using industry-standard algorithms</li>
          <li>Encrypted data transmission using HTTPS/TLS</li>
          <li>Regular security audits and updates</li>
          <li>Access controls and authentication requirements</li>
          <li>Secure backup and disaster recovery procedures</li>
        </ul>
        <p>
          <strong>Important:</strong> No method of transmission over the Internet or electronic storage is 100% secure. 
          While we strive to protect your information, we cannot guarantee absolute security.
        </p>

        <h2 className="h4 mt-4">5. Information Sharing and Disclosure</h2>
        <p>
          We do not sell, trade, or rent your personal information to third parties. We may share your information only 
          in the following circumstances:
        </p>
        <ul>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information</li>
          <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our Service 
          (e.g., hosting, storage, email delivery) under strict confidentiality agreements</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental request</li>
          <li><strong>Protection of Rights:</strong> To protect our rights, property, safety, or that of our users or the public</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (users will be notified)</li>
        </ul>

        <h2 className="h4 mt-4">6. Data Retention</h2>
        <p>
          We retain your information for as long as your account is active or as needed to provide the Service. 
          Specifically:
        </p>
        <ul>
          <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after closure</li>
          <li><strong>Education Records:</strong> Retained according to your preferences or for up to 7 years after account closure</li>
          <li><strong>Certificates:</strong> Retained while your account is active and for 90 days after account deletion</li>
          <li><strong>Log Data:</strong> Retained for up to 90 days for security and analytics purposes</li>
        </ul>
        <p>
          You may request earlier deletion of your data by contacting us, subject to legal retention requirements.
        </p>

        <h2 className="h4 mt-4">7. Your Rights and Choices</h2>
        <p>
          You have the following rights regarding your personal information:
        </p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Update or correct inaccurate information through your account settings</li>
          <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
          <li><strong>Export:</strong> Download your data in a portable format</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications (service notifications may still be sent)</li>
          <li><strong>Restriction:</strong> Request limitation of how we use your data</li>
        </ul>
        <p>
          To exercise these rights, please contact us at support@cectrack.com or use the account settings within the Service.
        </p>

        <h2 className="h4 mt-4">8. Cookies and Tracking Technologies</h2>
        <p>
          We use cookies and similar tracking technologies to:
        </p>
        <ul>
          <li>Maintain your login session</li>
          <li>Remember your preferences</li>
          <li>Analyze usage patterns and improve the Service</li>
          <li>Provide security features</li>
        </ul>
        <p>
          You can control cookies through your browser settings. However, disabling cookies may limit your ability to 
          use certain features of the Service.
        </p>

        <h2 className="h4 mt-4">9. Children's Privacy</h2>
        <p>
          Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal 
          information from children. If we become aware that we have collected information from a child without 
          parental consent, we will take steps to delete that information.
        </p>

        <h2 className="h4 mt-4">10. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence. 
          These countries may have different data protection laws. By using the Service, you consent to such transfers.
        </p>
        <p>
          We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
        </p>

        <h2 className="h4 mt-4">11. California Privacy Rights</h2>
        <p>
          California residents have additional rights under the California Consumer Privacy Act (CCPA):
        </p>
        <ul>
          <li>Right to know what personal information is collected, used, shared, or sold</li>
          <li>Right to delete personal information</li>
          <li>Right to opt-out of the sale of personal information (we do not sell personal information)</li>
          <li>Right to non-discrimination for exercising CCPA rights</li>
        </ul>
        <p>
          To exercise these rights, please contact us at support@cectrack.com.
        </p>

        <h2 className="h4 mt-4">12. European Privacy Rights (GDPR)</h2>
        <p>
          If you are located in the European Economic Area (EEA), you have additional rights under the General Data 
          Protection Regulation (GDPR):
        </p>
        <ul>
          <li>Right to access your personal data</li>
          <li>Right to rectification of inaccurate data</li>
          <li>Right to erasure ("right to be forgotten")</li>
          <li>Right to restrict processing</li>
          <li>Right to data portability</li>
          <li>Right to object to processing</li>
          <li>Right to lodge a complaint with a supervisory authority</li>
        </ul>
        <p>
          Our legal basis for processing your data includes: performance of contract, legitimate interests, and your consent.
        </p>

        <h2 className="h4 mt-4">13. Third-Party Links</h2>
        <p>
          The Service may contain links to third-party websites or services. We are not responsible for the privacy 
          practices of these third parties. We encourage you to review their privacy policies before providing any 
          personal information.
        </p>

        <h2 className="h4 mt-4">14. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material changes by:
        </p>
        <ul>
          <li>Posting the new Privacy Policy on this page</li>
          <li>Updating the "Last Updated" date</li>
          <li>Sending an email notification to registered users</li>
          <li>Displaying a prominent notice within the Service</li>
        </ul>
        <p>
          We encourage you to review this Privacy Policy periodically. Your continued use of the Service after changes 
          constitutes acceptance of the updated policy.
        </p>

        <h2 className="h4 mt-4">15. Contact Us</h2>
        <p>
          If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please 
          contact us at:
        </p>
        <p>
          <strong>CEC Track, Inc.</strong><br />
          Email: support@cectrack.com<br />
          Privacy Officer: privacy@cectrack.com
        </p>
        <p>
          We will respond to your inquiry within 30 days.
        </p>

        <h2 className="h4 mt-4">16. Data Protection Officer</h2>
        <p>
          For questions specifically related to data protection and privacy compliance, you may contact our Data 
          Protection Officer at: privacy@cectrack.com
        </p>
      </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
